import { useState } from 'react';
import { Combine, Minus, SigmaSquare, MousePointer2 } from 'lucide-react';
import { useCadStore, uid } from '../store/cadStore';
import { useThemeTokens } from '../theme';
import { booleanInWorker } from '../services/workRunner';
import { recordMutation } from '../services/learningService';

/* Tinkercad-style top Boolean toolbar: Union / Subtract / Intersect, plus
   cancel-select so the toolbar never fights the canvas gizmo. Boolean math is
   dispatched to the geometry Web Worker (async, non-blocking → 60 FPS). */
export default function BooleanToolbar() {
    const t = useThemeTokens();
    const setSelectedPart = useCadStore((s) => s.setSelectedPart);
    const hasSelection = useCadStore((s) => !!s.selectedPartId);
    const activeProjectId = useCadStore((s) => s.activeProjectId);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');

    const run = async (op) => {
        if (!hasSelection) return;
        const st = useCadStore.getState();
        setBusy(true); setNotice('');
        try {
            const oldParts = st.currentModel;
            const result = await booleanInWorker(oldParts, op);
            if (!Array.isArray(result)) throw new Error('Boolean failed.');
            const parts = result.map((p) => ({ ...p, id: p.id || uid() }));
            st.setCurrentModel(parts);
            setSelectedPart(parts.length ? parts[parts.length - 1].id : null);
            setNotice(`${op} applied (${parts.length} part${parts.length === 1 ? '' : 's'})`);
            recordMutation(activeProjectId, `boolean_${op}`, { parts });
        } catch (err) {
            console.error('Boolean failed:', err);
            setNotice('Boolean failed — see console.');
        } finally {
            setBusy(false);
        }
    };

    const OPS = [
        { id: 'union', icon: Combine, label: 'Union', tip: 'Merge all selected into one hull' },
        { id: 'subtract', icon: Minus, label: 'Subtract', tip: 'Carve selected out of larger part' },
        { id: 'intersect', icon: SigmaSquare, label: 'Intersect', tip: 'Keep where selected overlap' },
    ];

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 2,
            background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 12,
            boxShadow: t.shadow, padding: 4, flexShrink: 0,
        }}>
            {OPS.map(({ id, icon: Icon, label, tip }) => (
                <button key={id} onClick={() => run(id)} title={tip} disabled={busy || !hasSelection}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 9,
                        border: 'none', cursor: busy || !hasSelection ? 'default' : 'pointer',
                        background: busy ? t.surface : t.accentSoft, fontWeight: 700, fontSize: 12,
                        color: busy || !hasSelection ? t.textMuted : t.accent, opacity: !hasSelection ? 0.4 : 1,
                    }}>
                    <Icon size={14} />{label}
                </button>
            ))}
            <span style={{ margin: '0 3px', width: 1, height: 18, background: t.borderLight }} />
            <button onClick={() => setSelectedPart(null)} title="Cancel selection (Esc)" disabled={!hasSelection}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 9,
                    border: 'none', cursor: 'pointer', background: 'transparent', fontWeight: 600, fontSize: 12,
                    color: t.textSub, opacity: !hasSelection ? 0.4 : 1,
                }}>
                <MousePointer2 size={13} />Select
            </button>
            {notice && <span style={{ fontSize: 10.5, color: t.success, fontWeight: 600, marginLeft: 6 }}>{notice}</span>}
        </div>
    );
}